import { t as tr } from '../i18n.js'
import { usePref } from '../prefs.js'

/**
 * "Active only" (APM-12): one per-user preference shared by every task view,
 * so the choice follows the person from the tree to the list, the Gantt and
 * the calendar, and across devices, instead of resetting on each visit.
 *
 * What "active" means depends on the view. In Overview > Tasks it means in
 * progress (`isActive`): backlog and done are both hidden, unless "Show
 * backlog" lets the backlog back in. Everywhere else (list, Gantt, calendar) it
 * means not closed (`isOpen`): done and archived work is hidden. (Archived
 * work is also hidden with the filter off, unless "Show archived" is on.) Either way a hidden task stays visible while something under it
 * still qualifies. The views are tree-ordered and indent by depth, so dropping
 * that parent would leave the work underneath it hanging at an indent with
 * nothing above it.
 *
 * The board is deliberately not wired to this: its columns are the statuses,
 * so emptying the done one would take away the place you drag a task to
 * finish it.
 */
export function useActiveOnly(): [boolean, (v: boolean) => void] {
  const [on, set] = usePref('activeTasksOnly', false)
  return [on, set]
}

/**
 * Whether a task survives the filter. `on` false keeps everything. `backlog`
 * (Overview > Tasks only) lets backlog tasks back in, along with any done
 * parent still holding open work.
 */
export const isActive = (t: { statusCategory: string; subtreeActiveCount: number; subtreeCount: number; subtreeDoneCount: number }, on: boolean, backlog = false) =>
  !on || t.statusCategory === 'in_progress' || t.subtreeActiveCount > 0 ||
  (backlog && (t.statusCategory === 'todo' || t.subtreeDoneCount < t.subtreeCount))

/** Status categories that close a task. The server leaves archived work out of the subtree counts, so a parent of archived-only work drops too. */
const CLOSED = new Set(['done', 'archived'])
export const isClosed = (category: string) => CLOSED.has(category)

/** Whether a task survives the filter outside the overview: anything not closed, plus closed parents of open work. */
export const isOpen = (t: { statusCategory: string; subtreeCount: number; subtreeDoneCount: number }, on: boolean) =>
  !on || !CLOSED.has(t.statusCategory) || t.subtreeDoneCount < t.subtreeCount

/**
 * "Show archived" (one per-user preference shared by every task view, the
 * board included): archived work is put away, so it is hidden unless asked
 * for. An archived task with live work under it stays, to head that work;
 * `subtreeCount` already leaves archived descendants out.
 */
export function useShowArchived(): [boolean, (v: boolean) => void] {
  const [on, set] = usePref('showArchivedTasks', false)
  return [on, set]
}

/** Whether a task survives the archived filter. `show` true keeps everything. */
export const notArchived = (t: { statusCategory: string; subtreeCount: number }, show: boolean) =>
  show || t.statusCategory !== 'archived' || t.subtreeCount > 0

export function ShowArchivedToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline small" data-tip={tr("Also show tasks in an archived status. Remembered, and shared by the task views and the board.")}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} /> {tr("Show archived")}
    </label>
  )
}

/** "Show backlog": a remembered companion to "Active only" for the overview task list. */
export function useShowBacklog(): [boolean, (v: boolean) => void] {
  const [on, set] = usePref('showBacklogTasks', false)
  return [on, set]
}

export function ShowBacklogToggle({ value, onChange, disabled }: { value: boolean; onChange: (v: boolean) => void; disabled: boolean }) {
  return (
    <label className="inline small" data-tip={tr("With \"Active only\" on, also show tasks still in the backlog. Remembered.")}>
      <input type="checkbox" checked={value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {tr("Show backlog")}
    </label>
  )
}

/** `inProgress` is the overview's meaning of "active"; the default is the other views' "not closed". */
export function ActiveOnlyToggle({ value, onChange, inProgress = false }: { value: boolean; onChange: (v: boolean) => void; inProgress?: boolean }) {
  return (
    <label className="inline small" data-tip={inProgress
      ? tr("Show only tasks in progress, hiding the backlog and done. Remembered, and shared by the task views.")
      : tr("Hide tasks that are done. Remembered, and shared by the task views.")}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} /> {tr("Active only")}
    </label>
  )
}
